import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Drawer, { Field } from "@/components/Drawer";
import { StatusBadge, invoiceStatusMap } from "@/components/StatusBadge";
import { Plus, Trash2, Pencil, Wallet, X, Receipt } from "lucide-react";
import { fmtMoney, fmtDate } from "@/lib/format";
import { toast } from "sonner";

const empty = {
  customer_id: "", project_id: "", invoice_number: "", issue_date: "",
  due_date: "", items: [], status: "draft", notes: "",
};

const paymentEmpty = { amount: 0, method: "bank_transfer", reference: "", notes: "" };

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [paying, setPaying] = useState(null); // invoice
  const [payData, setPayData] = useState(paymentEmpty);

  const load = async () => {
    const [i, c, p] = await Promise.all([api.get("/invoices"), api.get("/customers"), api.get("/projects")]);
    setRows(i.data); setCustomers(c.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    const payload = {
      ...data,
      project_id: data.project_id || null,
      issue_date: data.issue_date || null,
      due_date: data.due_date || null,
      items: data.items.map((i) => ({
        ...i,
        quantity: Number(i.quantity) || 0,
        unit_price: Number(i.unit_price) || 0,
        tax_percent: Number(i.tax_percent) || 0,
      })),
    };
    try {
      if (drawer.mode === "edit") await api.patch(`/invoices/${drawer.data.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success("Saved");
      setDrawer(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete invoice?")) return;
    await api.delete(`/invoices/${id}`);
    load();
  };

  const recordPayment = async () => {
    try {
      await api.post(`/invoices/${paying.id}/payments`, {
        ...payData,
        amount: Number(payData.amount) || 0,
      });
      toast.success("Payment recorded");
      setPaying(null);
      setPayData(paymentEmpty);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Invoices"
        subtitle="Bill customers, track collections and outstanding dues."
        actions={
          <button className="btn-primary flex items-center gap-2" data-testid="add-invoice-btn"
            onClick={() => setDrawer({ mode: "create", data: { ...empty, items: [{ description: "", quantity: 1, unit_price: 0, tax_percent: 18 }], issue_date: new Date().toISOString().slice(0, 10) } })}>
            <Plus className="w-4 h-4" /> New Invoice
          </button>
        }
      />

      <div className="crm-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <Th>Invoice #</Th><Th>Customer</Th><Th>Project</Th><Th>Status</Th>
              <Th className="text-right">Total</Th><Th className="text-right">Paid</Th><Th className="text-right">Due</Th>
              <Th>Issued</Th><Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const due = (r.totals?.total || 0) - (r.paid_amount || 0);
              return (
                <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50/50" data-testid={`invoice-row-${r.id}`}>
                  <Td className="font-mono">{r.invoice_number || r.id.slice(0, 8)}</Td>
                  <Td>{customers.find((c) => c.id === r.customer_id)?.name || "—"}</Td>
                  <Td>{projects.find((p) => p.id === r.project_id)?.name || "—"}</Td>
                  <Td><StatusBadge value={r.status} map={invoiceStatusMap} /></Td>
                  <Td className="text-right font-mono">{fmtMoney(r.totals?.total)}</Td>
                  <Td className="text-right font-mono text-[#059669]">{fmtMoney(r.paid_amount)}</Td>
                  <Td className={`text-right font-mono ${due > 0 ? "text-red-600" : "text-slate-500"}`}>{fmtMoney(due)}</Td>
                  <Td className="text-xs text-slate-500">{fmtDate(r.issue_date)}</Td>
                  <Td className="text-right whitespace-nowrap">
                    <button className="btn-ghost !p-1.5" title="Record payment" data-testid={`pay-invoice-${r.id}`}
                      onClick={() => { setPaying(r); setPayData({ ...paymentEmpty, amount: due > 0 ? due : 0 }); }}>
                      <Wallet className="w-4 h-4 text-[#059669]" />
                    </button>
                    <button className="btn-ghost !p-1.5" onClick={() => setDrawer({ mode: "edit", data: { ...empty, ...r } })}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="btn-ghost !p-1.5 text-red-600" onClick={() => remove(r.id)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="p-10 text-center text-slate-500">
                <Receipt className="w-6 h-6 mx-auto mb-2 opacity-50" />
                No invoices yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {drawer && (
        <Drawer open onClose={() => setDrawer(null)} title={drawer.mode === "edit" ? "Edit Invoice" : "New Invoice"}
          wide testid="invoice-drawer"
          footer={
            <>
              <button className="btn-outline" onClick={() => setDrawer(null)}>Cancel</button>
              <button className="btn-primary" data-testid="invoice-save-btn" onClick={() => save(drawer.data)}>Save</button>
            </>
          }
        >
          <InvoiceForm data={drawer.data} onChange={(d) => setDrawer({ ...drawer, data: d })} customers={customers} projects={projects} />
        </Drawer>
      )}

      {paying && (
        <Drawer open onClose={() => setPaying(null)} title={`Record Payment · ${paying.invoice_number || paying.id.slice(0, 8)}`}
          testid="payment-drawer"
          footer={
            <>
              <button className="btn-outline" onClick={() => setPaying(null)}>Cancel</button>
              <button className="btn-accent" data-testid="payment-save-btn" onClick={recordPayment}>Record Payment</button>
            </>
          }
        >
          <div className="rounded-lg border border-slate-200 p-3 mb-4 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-mono">{fmtMoney(paying.totals?.total)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Paid</span><span className="font-mono text-[#059669]">{fmtMoney(paying.paid_amount)}</span></div>
            <div className="flex justify-between font-semibold pt-2 border-t border-slate-100 mt-2"><span>Balance</span><span className="font-mono">{fmtMoney((paying.totals?.total || 0) - (paying.paid_amount || 0))}</span></div>
          </div>
          <Field label="Amount *"><input className="input-base" type="number" value={payData.amount} data-testid="payment-amount" onChange={(e) => setPayData({ ...payData, amount: e.target.value })} /></Field>
          <Field label="Method">
            <select className="input-base" value={payData.method} onChange={(e) => setPayData({ ...payData, method: e.target.value })}>
              {["bank_transfer", "cash", "cheque", "upi", "card", "other"].map((k) => <option key={k} value={k}>{k.replace("_", " ")}</option>)}
            </select>
          </Field>
          <Field label="Reference"><input className="input-base" value={payData.reference} onChange={(e) => setPayData({ ...payData, reference: e.target.value })} /></Field>
          <Field label="Notes"><textarea className="input-base min-h-[60px]" value={payData.notes} onChange={(e) => setPayData({ ...payData, notes: e.target.value })} /></Field>

          {paying.payments?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Payment History</div>
              <div className="space-y-2">
                {paying.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm border border-slate-200 rounded-lg p-2">
                    <div>
                      <div className="font-mono">{fmtMoney(p.amount)}</div>
                      <div className="text-xs text-slate-500">{p.method} · {fmtDate(p.paid_at)}</div>
                    </div>
                    <div className="text-xs text-slate-500">{p.reference || ""}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Drawer>
      )}
    </div>
  );
}

function InvoiceForm({ data, onChange, customers, projects }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const items = data.items || [];
  const setItem = (i, k, v) => set("items", items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addItem = () => set("items", [...items, { description: "", quantity: 1, unit_price: 0, tax_percent: 18 }]);
  const removeItem = (i) => set("items", items.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((a, i) => a + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const tax = items.reduce((a, i) => a + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0) * ((Number(i.tax_percent) || 0) / 100), 0);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <Field label="Customer *">
          <select className="input-base" value={data.customer_id} data-testid="invoice-customer" onChange={(e) => set("customer_id", e.target.value)}>
            <option value="">— Select —</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Project">
          <select className="input-base" value={data.project_id || ""} onChange={(e) => set("project_id", e.target.value)}>
            <option value="">—</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Invoice Number"><input className="input-base" value={data.invoice_number || ""} data-testid="invoice-number" onChange={(e) => set("invoice_number", e.target.value)} /></Field>
        <Field label="Status">
          <select className="input-base" value={data.status} onChange={(e) => set("status", e.target.value)}>
            {Object.entries(invoiceStatusMap).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Field>
        <Field label="Issue Date"><input type="date" className="input-base" value={(data.issue_date || "").slice(0, 10)} onChange={(e) => set("issue_date", e.target.value)} /></Field>
        <Field label="Due Date"><input type="date" className="input-base" value={(data.due_date || "").slice(0, 10)} onChange={(e) => set("due_date", e.target.value)} /></Field>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Line Items</div>
          <button className="btn-ghost text-sm" onClick={addItem} data-testid="invoice-add-item"><Plus className="w-3.5 h-3.5 inline" /> Add line</button>
        </div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <input className="input-base col-span-5" placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} />
              <input className="input-base col-span-2" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
              <input className="input-base col-span-2" type="number" placeholder="Rate" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
              <input className="input-base col-span-2" type="number" placeholder="Tax %" value={it.tax_percent} onChange={(e) => setItem(i, "tax_percent", e.target.value)} />
              <button className="btn-ghost !p-1.5 text-red-600 col-span-1" onClick={() => removeItem(i)}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-3 rounded-lg border border-slate-200 p-3 bg-slate-50 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono">{fmtMoney(subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Tax</span><span className="font-mono">{fmtMoney(tax)}</span></div>
          <div className="flex justify-between font-semibold pt-2 border-t border-slate-200 mt-2"><span>Total</span><span className="font-mono">{fmtMoney(subtotal + tax)}</span></div>
        </div>
      </div>
      <Field label="Notes"><textarea className="input-base min-h-[60px]" value={data.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
    </>
  );
}

const Th = ({ children, className = "" }) => <th className={`px-4 py-3 text-left font-medium ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
